import { parseDate, type CalendarDate } from '@internationalized/date';
import {
  DateField as AriaDateField,
  DateInput,
  DateSegment,
  FieldError,
  I18nProvider,
  Label,
  Text,
} from 'react-aria-components';

import styles from './DateField.module.css';

export interface DateFieldProps {
  /** Always visible, above the field. */
  label: string;
  /** A calendar date as `YYYY-MM-DD`, or `null` when empty. Controlled when given. */
  value?: string | null;
  defaultValue?: string | null;
  /** Called with `YYYY-MM-DD` once day, month and year are all entered, or `null`. */
  onChange?: (value: string | null) => void;
  /** Earliest allowed date, `YYYY-MM-DD`. */
  minValue?: string;
  /** Latest allowed date, `YYYY-MM-DD`; for a date of birth, today. */
  maxValue?: string;
  description?: string;
  /** What is wrong and how to fix it. Setting it marks the field invalid. */
  errorMessage?: string;
  isRequired?: boolean;
  isDisabled?: boolean;
  autoFocus?: boolean;
  name?: string;
}

function toDate(value: string | null | undefined): CalendarDate | null | undefined {
  if (value === undefined) return undefined;
  if (value === null || value === '') return null;
  return parseDate(value);
}

/**
 * A calendar date typed as day, month and year, in that order whatever the browser's
 * language, because a month-first date of birth typed by a Kenyan receptionist is a
 * different patient. Values in and out are ISO `YYYY-MM-DD` strings; display elsewhere
 * uses `22 Sep 2026`.
 */
export function DateField({
  label,
  value,
  defaultValue,
  onChange,
  minValue,
  maxValue,
  description,
  errorMessage,
  isRequired,
  isDisabled,
  autoFocus,
  name,
}: DateFieldProps) {
  const current = toDate(value);
  const initial = toDate(defaultValue);
  const min = toDate(minValue);
  const max = toDate(maxValue);
  return (
    <I18nProvider locale="en-GB">
      <AriaDateField<CalendarDate>
        {...(current === undefined ? {} : { value: current })}
        {...(initial === undefined ? {} : { defaultValue: initial })}
        {...(min === undefined || min === null ? {} : { minValue: min })}
        {...(max === undefined || max === null ? {} : { maxValue: max })}
        {...(onChange === undefined
          ? {}
          : {
              onChange: (date: CalendarDate | null) => {
                onChange(date === null ? null : date.toString());
              },
            })}
        {...(isRequired === undefined ? {} : { isRequired })}
        {...(isDisabled === undefined ? {} : { isDisabled })}
        {...(autoFocus === undefined ? {} : { autoFocus })}
        {...(name === undefined ? {} : { name })}
        granularity="day"
        isInvalid={errorMessage !== undefined}
        className={styles.field}
      >
        <Label className={styles.label}>{label}</Label>
        <DateInput className={styles.input}>
          {(segment) => <DateSegment segment={segment} className={styles.segment} />}
        </DateInput>
        {description === undefined ? null : (
          <Text slot="description" className={styles.description}>
            {description}
          </Text>
        )}
        <FieldError className={styles.error}>{errorMessage}</FieldError>
      </AriaDateField>
    </I18nProvider>
  );
}
